'use client'

import { useCallback, useMemo } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

/** Sin valor por defecto compartido — cada página declara su propio mapa
 * clave interna → nombre real de query param (mismo motivo que `pageSize`/
 * `threshold` en `useInfiniteList`: no hay un valor común a todas las
 * integraciones). */
export interface UseListingFieldFiltersConfig<TKey extends string> {
  readonly paramNames: Record<TKey, string>
  /** Nombre del query param de página: se elimina al cambiar cualquier filtro
   * (cualquier cambio reinicia la paginación a la página 1). */
  readonly pageParam: string
}

export interface UseListingFieldFiltersResult<TKey extends string> {
  /** Valor actual de cada filtro, leído de la URL (cadena vacía si ausente). */
  readonly values: Record<TKey, string>
  /**
   * Actualiza un único filtro reflejándolo en la URL al instante. Elimina
   * también `pageParam` — cualquier cambio de filtro reinicia la paginación.
   */
  readonly setFilter: (key: TKey, value: string) => void
  /**
   * Igual que `setFilter`, pero aplica varios cambios a la vez en una sola
   * navegación — necesario cuando dos filtros deben cambiar juntos de forma
   * atómica (p. ej. `start_at`+`end_at` al cerrarse un rango de fechas): dos
   * llamadas sucesivas a `setFilter` partirían ambas del mismo `params` ya
   * leído (aún no re-renderizado tras la primera), así que la segunda
   * pisaría el cambio de la primera en vez de acumularse.
   */
  readonly setFilters: (updates: Partial<Record<TKey, string>>) => void
}

/**
 * Filtros de un listado gestionados en la URL (no en memoria), para que el
 * Server Component los lea y la URL quede compartible / con atrás-adelante /
 * superviviente a recargas — filtros de listado gestionados en la URL.
 * Genérico y reutilizable por cualquier página con sus propios filtros: no
 * conoce nombres de filtro concretos, solo el `paramNames` que le inyecta
 * cada página consumidora (mismo principio que `useInfiniteList`).
 */
export function useListingFieldFilters<TKey extends string>({
  paramNames,
  pageParam,
}: UseListingFieldFiltersConfig<TKey>): UseListingFieldFiltersResult<TKey> {
  const params = useSearchParams()
  const pathname = usePathname()
  const { replace } = useRouter()

  const values = useMemo(() => {
    const result = {} as Record<TKey, string>
    for (const key of Object.keys(paramNames) as TKey[]) {
      result[key] = params.get(paramNames[key]) ?? ''
    }
    return result
  }, [params, paramNames])

  const setFilters = useCallback(
    (updates: Partial<Record<TKey, string>>) => {
      const next = new URLSearchParams(params.toString())
      for (const key of Object.keys(updates) as TKey[]) {
        const value = updates[key]
        const paramName = paramNames[key]
        if (!value) next.delete(paramName)
        else next.set(paramName, value)
      }
      next.delete(pageParam)
      const qs = next.toString()
      replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [params, pathname, replace, paramNames, pageParam],
  )

  const setFilter = useCallback(
    (key: TKey, value: string) => setFilters({ [key]: value } as Partial<Record<TKey, string>>),
    [setFilters],
  )

  return { values, setFilter, setFilters }
}
